import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, Col, Row } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faClock, faExclamationTriangle, faFire, faHourglassHalf } from '@fortawesome/free-solid-svg-icons'
import 'animate.css'
import useSWRFetch from '../../helpers/hooks/useSWRFetch'
import { getRewardsSummary } from '../../helpers/api/rewards'
import useCountdown from '../rewards/useCountdown'
import { formatBonusPercent, formatDeadline, getCurrentPeriodStatus } from '../rewards/rewardsUtils'

// HO-TASK-283: racha de puntualidad y cuenta regresiva al cierre del pago
// puntual, en el dashboard del propietario. Usa la misma clave de SWR que la
// vista de Rewards, así que entre las dos pantallas se pide una sola vez.
//
// El estado del mes (pagado, en revisión, vencido) no se calcula acá: qué
// período cuenta para la racha y cuándo cierra son reglas del backend
// (StreakService, HO-TASK-266). Ver getCurrentPeriodStatus: con la API de hoy
// solo se sabe si el mes ya se pagó a tiempo; la cuenta regresiva aparece
// cuando el backend exponga current_period.

// Con racha activa y el período sin pagar, por debajo de este margen el
// aviso pasa a "racha en riesgo" (rojo). Antes es un recordatorio: marcar en
// rojo con dos semanas de plazo solo acostumbra a ignorar la alerta.
const AT_RISK_MS = 3 * 24 * 60 * 60 * 1000

// Mismo flujo que el botón "Pagar cuentas" de Mis cuentas por pagar.
const PAY_ROUTE = '/dashboard/my/finance/topays'
const REWARDS_ROUTE = '/dashboard/rewards'

const readStoredNumber = (key) => {
  try {
    const value = window.localStorage.getItem(key)
    return value === null ? null : Number(value)
  } catch (error) {
    return null
  }
}

const storeNumber = (key, value) => {
  try {
    window.localStorage.setItem(key, String(value))
  } catch (error) {
    // Sin almacenamiento solo se pierde la animación, el widget funciona igual.
  }
}

// Indica si la racha subió desde la última vez que el propietario vio el
// widget en este navegador, para celebrarlo una sola vez.
const useStreakAdvanced = (months, storageKey) => {
  const [advanced, setAdvanced] = useState(false)

  useEffect(() => {
    if (months === null || months === undefined) return
    const lastSeen = readStoredNumber(storageKey)
    setAdvanced(lastSeen !== null && months > lastSeen)
    storeNumber(storageKey, months)
  }, [months, storageKey])

  return advanced
}

const CountdownUnit = ({ value, label, color }) => (
  <div className={`text-center rounded-soft bg-soft-${color} px-2 py-1 mr-2`} style={{ minWidth: '3.5rem' }}>
    <div className={`fs-2 font-weight-semi-bold text-${color}`} style={{ lineHeight: 1.2 }}>{value}</div>
    <div className="fs--2 text-600">{label}</div>
  </div>
)

const Countdown = ({ remaining }) => {
  const { expired, ms, days, hours, minutes } = remaining

  if (expired) {
    return <p className="fs--1 text-600 mb-0">El plazo para el pago puntual de este mes ya cerró.</p>
  }

  const color = ms < AT_RISK_MS ? 'danger' : 'warning'

  return (
    <div className="d-flex align-items-center" role="timer" aria-label="Tiempo restante para pagar a tiempo">
      <CountdownUnit value={days} label={days === 1 ? 'día' : 'días'} color={color} />
      <CountdownUnit value={hours} label={hours === 1 ? 'hora' : 'horas'} color={color} />
      <CountdownUnit value={minutes} label="min" color={color} />
    </div>
  )
}

Countdown.propTypes = { remaining: PropTypes.object.isRequired }

// Mensaje, color y acciones de cada estado del período.
const getStreakState = ({ months, multiplier, period, remaining }) => {
  const hasStreak = months > 0
  const monthsText = `${months} ${months === 1 ? 'mes' : 'meses'}`
  const fee = period ? `tu cuota de ${period.label}` : ''
  const bonus = multiplier > 1 ? formatBonusPercent(multiplier) : null
  const deadlineText = period?.deadline_at ? formatDeadline(period.deadline_at) : null

  // Si el plazo vence con la pantalla abierta, se muestra como vencido hasta
  // que el backend actualice el estado.
  const status = period?.status === 'pending' && period.deadline_at && remaining.expired ? 'late' : period?.status

  switch (status) {
    case 'pending':
      if (hasStreak && remaining.ms < AT_RISK_MS) {
        return {
          tone: 'danger',
          icon: faExclamationTriangle,
          title: `¡Tu racha de ${monthsText} está en riesgo!`,
          text: `Paga ${fee} antes del ${deadlineText} para mantenerla${bonus ? ` y seguir ganando ${bonus} en Coins` : ''}.`,
          countdown: true,
          pay: true
        }
      }
      return hasStreak
        ? {
            tone: 'warning',
            icon: faFire,
            title: `Mantén tu racha de ${monthsText}`,
            text: `Paga ${fee} antes del ${deadlineText} para sumar un mes más${bonus ? ` y seguir ganando ${bonus} en Coins` : ''}.`,
            countdown: true,
            pay: true
          }
        : {
            tone: 'primary',
            icon: faFire,
            title: 'Empieza tu racha de puntualidad este mes',
            text: `Paga ${fee} antes del ${deadlineText} y gana XP extra. Cada mes puntual seguido suma a tu racha y a tus premios.`,
            countdown: true,
            pay: true
          }
    case 'in_review':
      return {
        tone: 'info',
        icon: faHourglassHalf,
        title: 'Tu pago está en revisión',
        text: `Registraste el pago de ${fee}. Cuando la administración lo apruebe verás reflejado tu avance en la racha.`
      }
    case 'on_time':
      return {
        tone: 'success',
        icon: faCheckCircle,
        title: `¡Pagaste ${fee} a tiempo!`,
        text: bonus
          ? `Tu racha sigue viva y estás ganando ${bonus} en Coins. Nos vemos el próximo mes.`
          : 'Tu racha sigue sumando. Nos vemos el próximo mes.'
      }
    case 'late':
      return {
        tone: 'warning',
        icon: faClock,
        title: `El pago de ${fee} no llegó a tiempo`,
        text: 'Tu racha se reinició. Si aún tienes cuentas pendientes, ponte al día y paga a tiempo el próximo mes para empezar una nueva.',
        pay: true
      }
    default:
      return hasStreak
        ? {
            tone: 'success',
            icon: faFire,
            title: `Llevas ${monthsText} pagando a tiempo`,
            text: 'Sigue así para mantener tu racha y tus beneficios.'
          }
        : {
            tone: 'primary',
            icon: faFire,
            title: 'Empieza tu racha de puntualidad',
            text: 'Paga tu cuota a tiempo cada mes para ganar XP extra y más Homelan Coins.'
          }
  }
}

const RewardsStreakWidget = () => {
  const { property } = useSelector((state) => state.auth.user)
  const { selectedProperty } = useSelector((state) => state.property)
  const { data, error } = useSWRFetch('rewards-summary', getRewardsSummary)

  const summary = data?.data
  const months = summary ? Number(summary.current_streak_months) || 0 : null
  const multiplier = Number(summary?.streak_multiplier) || 1
  const period = summary ? getCurrentPeriodStatus(summary) : null
  const advanced = useStreakAdvanced(months, `rewards:lastSeenStreak:${selectedProperty || property || 'me'}`)
  // Vive acá y no en <Countdown>: el paso a "racha en riesgo" depende del
  // tiempo restante, así que el widget entero se reevalúa cada minuto.
  const remaining = useCountdown(period?.status === 'pending' ? period.deadline_at : null)

  // El widget es un complemento del dashboard: si Rewards no responde, no
  // se muestra nada en vez de meter un error en la pantalla de inicio.
  if (error || !summary) return null

  const state = getStreakState({ months, multiplier, period, remaining })

  return (
    <Card className={`mb-3 border-left border-${state.tone}`} style={{ borderLeftWidth: '4px' }}>
      <CardBody>
        <Row className="align-items-center">
          {/* En móvil el ícono va al lado de los meses; desde md, apilados. */}
          <Col xs={12} md="auto" className="d-flex d-md-block align-items-center text-md-center mb-3 mb-md-0">
            <div
              className={`d-flex flex-center rounded-circle flex-shrink-0 mr-3 mx-md-auto ${months > 0 ? 'bg-soft-danger text-danger' : 'bg-soft-secondary text-500'} ${advanced ? 'animate__animated animate__tada' : ''}`}
              style={{ width: '4rem', height: '4rem' }}
            >
              <FontAwesomeIcon icon={faFire} className="fs-4" />
            </div>
            <div className="mt-md-1 mr-2 mr-md-0">
              <span className="fs-1 font-weight-semi-bold text-900">{months}</span>{' '}
              <span className="fs--1 text-600">{months === 1 ? 'mes' : 'meses'}</span>
              {advanced && (
                <span className="badge badge-soft-success rounded-capsule ml-1 animate__animated animate__bounceIn">+1</span>
              )}
            </div>
            {multiplier > 1 && (
              <span className="badge badge-soft-success rounded-capsule fs--2" title="Bono activo sobre las Coins que ganas">
                {formatBonusPercent(multiplier)} Coins
              </span>
            )}
          </Col>

          <Col className="mb-3 mb-lg-0" style={{ minWidth: '14rem' }}>
            <h5 className={`mb-1 text-${state.tone === 'primary' ? '900' : state.tone}`}>
              <FontAwesomeIcon icon={state.icon} className="mr-2" />
              {state.title}
            </h5>
            <p className="fs--1 text-700 mb-2">{state.text}</p>
            {state.countdown && <Countdown remaining={remaining} />}
          </Col>

          <Col xs={12} lg="auto" className="d-flex flex-wrap flex-lg-column">
            {state.pay && (
              <Button tag={Link} to={PAY_ROUTE} color={state.tone === 'danger' ? 'danger' : 'primary'} size="sm" className="mb-2 mr-2 mr-lg-0">
                Pagar ahora
              </Button>
            )}
            <Button tag={Link} to={REWARDS_ROUTE} color="falcon-default" size="sm" className="mb-2">
              Ver mis Rewards
            </Button>
          </Col>
        </Row>
      </CardBody>
    </Card>
  )
}

export default RewardsStreakWidget
